// src/context/NotificationContext.tsx - UPDATED WITH SOUND CONTROLS
import React, { createContext, useState, useEffect, ReactNode, useCallback, useRef } from 'react';
import { useAuth } from '../hooks/useAuth';
import { useSound } from '../hooks/useSound';
import toast from 'react-hot-toast';
import { notificationsAPI } from '../api/notifications';
import {
  getNotificationMessage,
  getNotificationTitle,
  getToastConfig,
} from '../constants/notifications';
import type { Notification, NotificationContextType } from '../types/notification.types';

// Create context
export const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

// Provider component
export const NotificationProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  
  // Use the enhanced sound hook
  const { soundEnabled, playSound, toggleSound, changeVolume, volume } = useSound({
    soundEnabled: true,
    defaultVolume: 0.7
  });

  const sseCleanupRef = useRef<(() => void) | null>(null);
  const pollingIntervalRef = useRef<NodeJS.Timeout>();
  const processedNotificationsRef = useRef<Set<string>>(new Set());

  // Setup SSE for real-time notifications
  useEffect(() => {
    if (isAuthenticated && user?._id) {
      if (sseCleanupRef.current) {
        sseCleanupRef.current();
      }

      try {
        console.log(`🔌 Setting up SSE for user: ${user._id}`);
        sseCleanupRef.current = notificationsAPI.listenForNotifications(user._id, (newNotification) => {
          console.log('📨 New notification received:', newNotification);

          // Prevent duplicate processing
          if (processedNotificationsRef.current.has(newNotification._id)) {
            return;
          }
          processedNotificationsRef.current.add(newNotification._id);

          setNotifications(prev => {
            if (prev.some(n => n._id === newNotification._id)) {
              return prev;
            }
            return [newNotification, ...prev];
          });

          if (!newNotification.isRead) {
            const message = newNotification.message || getNotificationMessage(newNotification.type);
            const title = newNotification.title || getNotificationTitle(newNotification.type);
            const toastConfig = getToastConfig(newNotification.type, message);

            // Show toast notification
            toast(message, {
              ...toastConfig,
              onClick: newNotification.orderId ? () => {
                const orderId = typeof newNotification.orderId === 'string' 
                  ? newNotification.orderId 
                  : newNotification.orderId?._id;
                if (orderId) {
                  window.location.href = `/order/${orderId}`;
                }
              } : undefined
            });

            // Play sound for notification
            console.log(`🔊 Playing sound for notification type: ${newNotification.type}`);
            playSound(newNotification.type);
          }

          // Clean up processed IDs after 1 minute
          setTimeout(() => {
            processedNotificationsRef.current.delete(newNotification._id);
          }, 60000);
        });
      } catch (error) {
        console.error('Error setting up SSE:', error);
      }
    }

    return () => {
      if (sseCleanupRef.current) {
        sseCleanupRef.current();
      }
    };
  }, [isAuthenticated, user?._id, playSound]);

  // Fetch notifications from API
  const fetchNotifications = useCallback(async () => {
    if (!isAuthenticated || !user?._id) {
      setNotifications([]);
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await notificationsAPI.getMyNotifications({ limit: 50 });

      if (response.data.success && response.data.data) {
        let fetchedNotifications: Notification[] = [];

        if (Array.isArray(response.data.data)) {
          fetchedNotifications = response.data.data;
        } else if (response.data.data && 'notifications' in response.data.data) {
          fetchedNotifications = (response.data.data as { notifications: Notification[] }).notifications;
        }

        fetchedNotifications.sort((a, b) =>
          new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
        );

        setNotifications(fetchedNotifications);
        console.log(`📬 Fetched ${fetchedNotifications.length} notifications`);
      }
    } catch (err) {
      console.error('Error fetching notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated, user?._id]);

  // Load notifications on mount and when user changes
  useEffect(() => {
    if (isAuthenticated) {
      fetchNotifications();
    } else {
      setNotifications([]);
    }

    pollingIntervalRef.current = setInterval(() => {
      if (isAuthenticated) {
        fetchNotifications();
      }
    }, 30000);

    return () => {
      if (pollingIntervalRef.current) {
        clearInterval(pollingIntervalRef.current);
      }
    };
  }, [fetchNotifications, isAuthenticated]);

  // Add new notification via API
  const addNotification = useCallback(async (
    notification: Omit<Notification, '_id' | 'createdAt' | 'updatedAt' | 'userId'>
  ) => {
    if (!isAuthenticated || !user?._id) {
      return;
    }

    try {
      const response = await notificationsAPI.create({
        ...notification,
        userId: user._id,
      });

      if (response.data.success && response.data.data) {
        const newNotification = response.data.data as Notification;
        setNotifications(prev => [newNotification, ...prev]);

        const message = newNotification.message || getNotificationMessage(notification.type);
        const toastConfig = getToastConfig(notification.type, message);
        toast(message, toastConfig);
        playSound(notification.type);
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.log('Notification service unavailable (non-critical)');
      }
    }
  }, [isAuthenticated, user?._id, playSound]);

  // Mark a single notification as read
  const markAsRead = useCallback(async (id: string) => {
    setNotifications(prev =>
      prev.map(notif =>
        notif._id === id ? { ...notif, isRead: true } : notif
      )
    );

    try {
      await notificationsAPI.markAsRead(id);
    } catch (error) {
      console.error('Error marking notification as read:', error);
      setNotifications(prev =>
        prev.map(notif =>
          notif._id === id ? { ...notif, isRead: false } : notif
        )
      );
      toast.error('Failed to mark notification as read');
    }
  }, []);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!isAuthenticated || !user?._id) return;

    const previousNotifications = [...notifications];
    setNotifications(prev =>
      prev.map(notif => ({ ...notif, isRead: true }))
    );

    try {
      await notificationsAPI.markAllAsRead();
      toast.success('All notifications marked as read');
    } catch (error) {
      console.error('Error marking all as read:', error);
      setNotifications(previousNotifications);
      toast.error('Failed to mark all as read');
    }
  }, [isAuthenticated, user?._id, notifications]);

  // Delete a notification
  const deleteNotification = useCallback(async (id: string) => {
    const previousNotifications = [...notifications];
    setNotifications(prev => prev.filter(notif => notif._id !== id));

    try {
      await notificationsAPI.delete(id);
    } catch (error) {
      console.error('Error deleting notification:', error);
      setNotifications(previousNotifications);
      toast.error('Failed to delete notification');
    }
  }, [notifications]);

  // Clear all notifications
  const clearNotifications = useCallback(async () => {
    if (!isAuthenticated || !user?._id) return;

    const previousNotifications = [...notifications];
    setNotifications([]);

    try {
      await notificationsAPI.deleteAll();
      toast.success('All notifications cleared');
    } catch (error) {
      console.error('Error clearing notifications:', error);
      setNotifications(previousNotifications);
      toast.error('Failed to clear notifications');
    }
  }, [isAuthenticated, user?._id, notifications]);

  const unreadCount = notifications.filter(n => !n.isRead).length;

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    loading,
    error,
    addNotification,
    markAsRead,
    markAllAsRead,
    clearNotifications,
    playSound,
    fetchNotifications,
    deleteNotification,
    soundEnabled,
    toggleSound,
    volume,
    changeVolume,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};